import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BasilArrowRight } from "@/components/icons/basil";
import { cn } from "@shared/lib/utils";
import { CollectionGrid } from "@/components/collection/CollectionGrid";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { ProfileAvatar } from "@/components/layout/ProfileCard";
import { profileDisplay, useProfile } from "@/components/layout/useProfile";
import { SellSection } from "@/components/marketplace/SellSection";
import { useMarketplace } from "@/components/marketplace/useMarketplace";
import { SpotlightCard } from "@/components/renewal/fx/SpotlightCard";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { GlowButton } from "@/components/ui/GlowButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { getKycStatus, listRedemptions } from "@/lib/renewal/cardActions";
import { useMissions } from "@/lib/renewal/missions";
import { MOCK_INVENTORY_PACKS } from "@/lib/renewal/shop";

type ProfileTab = "collection" | "listings";

const TABS: { key: ProfileTab; label: string }[] = [
  { key: "collection", label: "Collection" },
  { key: "listings", label: "Listings" },
];

function StatCard({
  label,
  value,
  valueClassName,
  loading,
  note,
}: {
  label: string;
  value: string;
  valueClassName: string;
  loading?: boolean;
  note?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-line bg-(--ty-bg-2) px-4 py-3.5 sm:px-5 sm:py-4">
      <p className="font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-ink-mute">
        {label}
        {note ? <span className="ml-1.5 normal-case text-white/55">· {note}</span> : null}
      </p>
      {loading ? (
        <Skeleton className="h-6 w-16" />
      ) : (
        <p className={cn("font-mono text-xl font-medium tabular-nums sm:text-2xl", valueClassName)}>{value}</p>
      )}
    </div>
  );
}

/** My Hub 진입 셀 — 눈썹 라벨 + mono tnum 수치 + BasilArrowRight, 44px 타깃 (TodayStrip Cell 문법). */
function HubCell({
  to,
  label,
  value,
  dot,
}: {
  to: string;
  label: string;
  value: string;
  dot?: boolean;
}) {
  return (
    <SpotlightCard className="h-full rounded-2xl">
      <Link
        to={to}
        aria-label={`${label} — ${value}`}
        className="flex h-full min-h-11 items-center justify-between gap-4 rounded-2xl border border-line bg-(--ty-bg-2) px-4 py-3 outline-none transition-colors duration-200 hover:border-line-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70"
      >
        <span className="flex items-center gap-2">
          <span className="ty-eyebrow text-[10px]">{label}</span>
          {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-danger" />}
        </span>
        <span className="flex items-center gap-3">
          <span className="font-mono text-sm font-medium tabular-nums text-ink">{value}</span>
          <BasilArrowRight className="size-3.5 shrink-0 text-ink-mute" aria-hidden />
        </span>
      </Link>
    </SpotlightCard>
  );
}

/** Collection tab — same gallery body as /collection, own filter/selection state.
 * The data hook lives in ProfilePage (not here) so flipping to Listings and back
 * doesn't refire list_agent_cards on every tab remount. */
function CollectionTab({ data }: { data: ReturnType<typeof useCollectionData> }) {
  const { authenticated, loading, error, state, reload } = data;
  const { signIn } = useProfile();

  return (
    <CollectionGrid
      authenticated={authenticated}
      loading={loading}
      error={error}
      state={state}
      onSignIn={() => signIn("/profile")}
      onRetry={reload}
    />
  );
}

/** Listings tab — renders only SellSection, which consumes my card instances;
 * `listings: false` skips the public list_market_listings + stats fetch that
 * would otherwise fire (and be thrown away) on every tab open. */
function ListingsTab() {
  const {
    authenticated,
    sessionPending,
    instances,
    instancesLoading,
    instancesError,
    refreshInstances,
    pending,
    list,
    cancel,
  } = useMarketplace({ listings: false });

  return (
    <SellSection
      authenticated={authenticated}
      sessionPending={sessionPending}
      instances={instances}
      loading={instancesLoading}
      error={instancesError}
      onRetry={() => void refreshInstances()}
      pending={pending}
      onList={list}
      onCancel={cancel}
    />
  );
}

/** /profile — identity header, IP/RP/Rank stat cards, Collection/Listings tabs. */
export function ProfilePage() {
  const { user, authenticated, sessionPending, summary, ensureSummary, signIn } = useProfile();
  const [tab, setTab] = useState<ProfileTab>("collection");
  // Hoisted above the tab switch — survives Collection↔Listings flips, so the
  // collection fetch runs once per page visit instead of once per flip.
  const collection = useCollectionData();
  const { missions } = useMissions();

  useEffect(() => {
    if (authenticated) ensureSummary();
  }, [authenticated, ensureSummary]);

  if (sessionPending) {
    return (
      <div className="flex items-center gap-4">
        <Skeleton className="size-16 rounded-full" />
        <Skeleton className="h-6 w-32 rounded" />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <GlassPanel className="flex flex-col items-center gap-2.5 px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
        <p className="text-xs text-ink-dim">Sign in to view your profile — balances, collection, listings.</p>
        <GlowButton onClick={() => signIn("/profile")} className="shrink-0">
          Sign in with Telegram
        </GlowButton>
      </GlassPanel>
    );
  }

  const display = profileDisplay(user);
  const summaryLoading = summary === null;
  const unopened = MOCK_INVENTORY_PACKS.reduce((sum, pack) => sum + pack.count, 0);
  const claimable = missions.filter((m) => m.status === "claimable").length;
  const redemptionCount = listRedemptions().length;
  const kycStatus = getKycStatus();
  const kycLabel =
    kycStatus === "verified"
      ? "Verified"
      : kycStatus === "pending"
        ? "In review"
        : kycStatus === "rejected"
          ? "Rejected"
          : "Not started";

  return (
    <div className="space-y-8 sm:space-y-10">
      <div className="flex items-center gap-4">
        <ProfileAvatar display={display} className="size-16 text-xl" />
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-medium text-ink sm:text-2xl">{display.name}</h1>
          {display.handle ? (
            <p className="truncate font-mono text-xs text-ink-mute">{display.handle}</p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          label="IP"
          value={(summary?.ip ?? 0).toLocaleString()}
          valueClassName="text-accent"
          loading={summaryLoading}
        />
        <StatCard
          label="RP"
          value={(summary?.rp ?? 0).toLocaleString()}
          valueClassName="text-yellow"
          loading={summaryLoading}
        />
        <StatCard label="Rank" value="—" valueClassName="text-ink-mute" note="coming soon" />
      </div>

      {/* My Hub — 슬림 내비에서 빠진 /profile 하위 목적지 진입점 (계층 라우팅 이후 6셀) */}
      <section aria-label="My hub">
        <p className="ty-eyebrow mb-3">My Hub</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          <HubCell to="/profile/packs" label="My Packs" value={`${unopened} unopened`} />
          <HubCell
            to="/profile/collection"
            label="Collection"
            value={
              collection.state
                ? `${collection.state.ownedCount}/${collection.state.totalCount} collected`
                : "—"
            }
          />
          <HubCell
            to="/profile/missions"
            label="Missions"
            value={`${claimable} to claim`}
            dot={claimable > 0}
          />
          <HubCell
            to="/profile/wallet"
            label="Wallet"
            value={
              summaryLoading
                ? "—"
                : `${(summary?.ip ?? 0).toLocaleString()} IP · ${(summary?.rp ?? 0).toLocaleString()} RP`
            }
          />
          {/* 계층 이설분(구 /redemptions·/kyc) — 숨은 라우트가 되지 않게 허브에 진입점 유지 */}
          <HubCell
            to="/profile/redemptions"
            label="Redemptions"
            value={`${redemptionCount} request${redemptionCount === 1 ? "" : "s"}`}
          />
          <HubCell
            to="/profile/kyc"
            label="Account / KYC"
            value={kycLabel}
            dot={kycStatus === "rejected"}
          />
        </div>
      </section>

      <div role="tablist" aria-label="Profile sections" className="flex gap-6 border-b border-line">
        {TABS.map((item) => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.key)}
              className={cn(
                "min-h-11 border-b-2 px-0.5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.14em] transition-colors duration-200",
                active ? "border-accent text-ink" : "border-transparent text-ink-mute hover:text-ink",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === "collection" ? <CollectionTab data={collection} /> : <ListingsTab />}
    </div>
  );
}
